import { useState, useEffect } from 'react';
import Strings from '../../translations/strings';
import logoImg from '../../img/logoRed.svg';
import * as legacyApi from '../../api/legacyApi';
import styles from './MainComponent.module.css';
import LanguageSwitch from '../LanguageSwitch/LanguageSwitch';
import CardContainer from '../Card/CardContainer/CardContainer';
import RssFeed from '../RssFeed/RssFeed/RssFeed';
import VideoCarouselContainer from '../VideoCarousel/VideoCarouselContainer/VideoCarouselContainer';
import Socials from '../Socials/Socials';
import commonDataConnection from '../../api/commonDataConnection';
import { ErrorMessage } from '@eway-crm/gui';
import { ReturnCodes } from '@eway-crm/connector';

interface IGetVersion {
    InfoResponse: IInfoResponse;
}

interface IInfoResponse {
    ReturnCode: {
        _text: string;
    };
    Description?: {
        _text: string;
    };
    WebServiceVersion?: {
        _text: string;
    };
    eWayVersion?: {
        _text: string;
    };
}

const MainComponent = () => {
    const [language, setLanguage] = useState(() => Strings.getLanguage());
    const [versionData, setVersionData] = useState<IInfoResponse | null>(null);
    const [versionError, setVersionError] = useState<(() => string) | null>(null);
    const [isWebServiceMoved, setIsWebServiceMoved] = useState(false);
    const [versionErrorData, setVersionErrorData] = useState<string[] | null>(null);

    const eWayVersion = versionData?.eWayVersion?._text || '';
    // Match only first two numbers of a version:
    const eWayVersionShort = eWayVersion?.match(/^(\d+\.\d+)/)?.pop() || '';
    const WebServiceVersion = versionData?.WebServiceVersion?._text;

    const handleGetVersionSuccess = (data: IGetVersion) => {
        const returnCode = data.InfoResponse.ReturnCode._text
        const description = data.InfoResponse.Description?._text;
        if (returnCode === ReturnCodes.rcSuccess) {
            setVersionData(data.InfoResponse);
        } else if (returnCode === ReturnCodes.rcWebServiceMoved) {
            setIsWebServiceMoved(true);
            setVersionErrorData(description ? [description] : ['rcWebServiceMoved']);
        } else {
            // Only way to store function in state is to wrap it in anonymous fn
            setVersionError(() => () => Strings.components.main.errorWS);
            setVersionErrorData(description ? [description] : null);
        }
    };

    const handleGetVersionError = () => {
        setVersionError(() => () => Strings.components.main.errorRequest);
        setVersionErrorData(['Request has returned an error while retrieving version data.']);
    };

    useEffect(() => {
        legacyApi.callWsGet('GetInfo', handleGetVersionSuccess, handleGetVersionError);
    }, []);

    useEffect(() => {
        document.title = Strings.pageTitle;
    }, [language]);

    const onLanguageSwitch = (newLang: string) => {
        Strings.setLanguage(newLang);
        setLanguage(newLang);
    };

    return isWebServiceMoved ? (
        <ErrorMessage
            icon="error"
            techData={versionErrorData}
            customStrings={{
                seeMore: Strings.components.main.errorSeeMore,
                seeLess: Strings.components.main.errorSeeLess,
            }}
        >
            {Strings.components.main.errorWebServiceMoved}
        </ErrorMessage>
    ) : (
        <>
            <LanguageSwitch language={language} onLanguageSwitch={onLanguageSwitch} />
            <div className={styles.main}>
                <header className={`${styles.header} ${versionError && styles.headerWithError}`}>
                    <img className={styles.logo} src={logoImg} alt="eWay-CRM logo" />
                    <h1 className={styles.title}>
                        {eWayVersionShort
                            ? `${Strings.components.main.titleLoaded} ${eWayVersionShort}`
                            : Strings.components.main.title}
                    </h1>
                    {versionError && (
                        <ErrorMessage
                            icon="error"
                            techData={versionErrorData}
                            customStrings={{
                                seeMore: Strings.components.main.errorSeeMore,
                                seeLess: Strings.components.main.errorSeeLess,
                            }}
                            className={styles.error}
                        >{`${Strings.components.main.errorTitle}. ${versionError()}.`}</ErrorMessage>
                    )}
                </header>

                <CardContainer eWayVersion={eWayVersion} />

                <VideoCarouselContainer commonDataConnection={commonDataConnection} />

                <RssFeed language={language} />

                <Socials />

                <footer className={styles.footer}>
                    {versionData && <div>{`Outlook: ${eWayVersion} | Web Service: ${WebServiceVersion}`}</div>}
                    <a
                        className={styles.footerLink}
                        href={`${legacyApi.getBaseUrl()}/eWayWS.asmx/CheckStatus`}
                        rel="noreferrer noopener"
                        target="_blank"
                    >
                        {Strings.components.main.statusWS}
                    </a>
                </footer>
            </div>
        </>
    );
};

export default MainComponent;
